import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, LogIn, UserPlus, MailCheck, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { login, register, forgotPassword, startGoogleSignIn, claimBrowserForUser, afterSignInPath } from '../../lib/authApi';
import { pendingReferralCode, forgetReferralCode } from '../../lib/billingApi';
import { apiErrorMessage } from '../../lib/projectApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';

/** Google's "G" mark (brand colours) for the sign-in button. */
const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const field = 'h-10 bg-background border-input text-sm';
const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

/**
 * The sign-in / create-account / forgot-password card. Controlled `mode` ('signin' | 'signup' | 'forgot') so the page
 * around it can switch modes too; everything else (fields, submit, errors) lives here. On success the user is stored
 * in AuthContext and the browser is claimed for the account, then we go to the editor (or the shared publication).
 * `frameless` drops the card's own border / background (the landing page draws the frame as a resizable text box);
 * `notice` renders above the form, `footer` below it.
 */
export const AuthCard = ({ mode, onModeChange, initialError = '', className = '', frameless = false, notice = null, footer = null }) => {
  const { setUser, clearExpiredNotice } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sentTo, setSentTo] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError);
  const setMode = (m) => { onModeChange(m); setError(''); setSentTo(''); };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (busy || !email) return;
    setError('');
    setBusy(true);
    try { await forgotPassword(email); setSentTo(email.trim()); } catch (err) { setError(await apiErrorMessage(err, 'The reset link could not be sent.')); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      const me = mode === 'signin' ? await login({ email, password }) : await register({ email, password, name, referralCode: pendingReferralCode() });
      if (mode !== 'signin' || me.referral?.referred_by) forgetReferralCode(); // the invite is on the account now (or this account is not new)
      const switched = await claimBrowserForUser(me.user_id);
      clearExpiredNotice();
      setUser(me);
      const invited = mode !== 'signin' && me.referral?.referred_by;
      toast.success(mode === 'signin' ? `Welcome back${me.name ? `, ${me.name.split(' ')[0]}` : ''}` : 'Your account is ready', { description: switched ? 'Open publications from the previous account on this browser were closed.' : invited ? 'Your friend\u2019s invitation is on your account — your discount is applied when you pick a plan.' : 'Your publications, presets and fonts are saved to this account.', id: 'auth' });
      navigate(afterSignInPath(location.search), { replace: true });
    } catch (err) {
      setError(await apiErrorMessage(err, mode === 'signin' ? 'Could not sign you in.' : 'Could not create the account.'));
    } finally { setBusy(false); }
  };

  return (
    <div className={`${frameless ? '' : 'rounded-xl border border-border bg-card text-card-foreground shadow-sm'} p-5 sm:p-6 text-left ${className}`} data-testid="auth-card">
      {notice}
      {mode === 'forgot' && (
        <div data-testid="auth-forgot-panel">
          <button type="button" onClick={() => setMode('signin')} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="auth-forgot-back"><ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />Back to sign in</button>
          <h2 className="mt-3 text-lg font-semibold tracking-tight" style={heading}>Forgot your password?</h2>
          {sentTo ? (
            <div className="mt-3 space-y-4" data-testid="auth-forgot-sent">
              <Alert className="border-border bg-background">
                <AlertDescription className="text-sm flex items-start gap-2">
                  <MailCheck className="h-4 w-4 shrink-0 text-primary mt-0.5" aria-hidden="true" />
                  <span>If <span className="font-medium text-foreground">{sentTo}</span> has a WannaPublish account, a reset link is on its way. It works once and expires in 1 hour — check your spam folder if it does not arrive.</span>
                </AlertDescription>
              </Alert>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" className="h-10 text-sm" onClick={() => setSentTo('')} data-testid="auth-forgot-again">Send again</Button>
                <Button type="button" className="h-10 text-sm" onClick={() => setMode('signin')} data-testid="auth-forgot-done">Back to sign in</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="mt-3 space-y-3.5" noValidate data-testid="auth-forgot-form">
              <p className="text-sm text-muted-foreground">Enter the email you signed up with and we will send you a link to choose a new password.</p>
              <div className="space-y-1.5">
                <Label htmlFor="auth-forgot-email" className="text-xs">Email</Label>
                <Input id="auth-forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com" required autoFocus className={field} aria-invalid={!!error || undefined} data-testid="auth-forgot-email-input" />
              </div>
              {error && <p role="alert" className="text-xs text-destructive" data-testid="auth-error">{error}</p>}
              <Button type="submit" className="w-full h-11 text-sm font-semibold" disabled={busy || !email} data-testid="auth-forgot-submit">{busy ? 'Sending…' : 'Send reset link'}</Button>
            </form>
          )}
        </div>
      )}
      {mode !== 'forgot' && (
        <Tabs value={mode} onValueChange={(v) => setMode(v)}>
          <TabsList className="grid w-full grid-cols-2 h-10">
            <TabsTrigger value="signin" className="text-sm" data-testid="auth-tab-signin"><LogIn className="h-4 w-4 mr-1.5" aria-hidden="true" />Sign in</TabsTrigger>
            <TabsTrigger value="signup" className="text-sm" data-testid="auth-tab-signup"><UserPlus className="h-4 w-4 mr-1.5" aria-hidden="true" />Create account</TabsTrigger>
          </TabsList>

          <Button type="button" variant="outline" className="mt-5 w-full h-11 gap-2.5 text-sm font-medium border-input bg-background hover:bg-accent/40" onClick={startGoogleSignIn} data-testid="auth-google-button">
            <GoogleMark />Continue with Google
          </Button>

          <div className="my-4 flex items-center gap-3 text-[11px] uppercase tracking-wide text-muted-foreground" aria-hidden="true"><span className="h-px flex-1 bg-border" />or with email<span className="h-px flex-1 bg-border" /></div>

          <form onSubmit={submit} className="space-y-3.5" noValidate data-testid="auth-form">
            <TabsContent value="signup" className="m-0">
              <div className="space-y-1.5">
                <Label htmlFor="auth-name" className="text-xs">Name</Label>
                <Input id="auth-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="Your name" className={field} data-testid="auth-name-input" />
              </div>
            </TabsContent>
            <div className="space-y-1.5">
              <Label htmlFor="auth-email" className="text-xs">Email</Label>
              <Input id="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com" required className={field} aria-invalid={!!error || undefined} data-testid="auth-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="auth-password" className="text-xs">Password</Label>
              <div className="relative">
                <Input id="auth-password" type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} placeholder={mode === 'signin' ? 'Your password' : 'At least 8 characters'} minLength={8} required className={`${field} pr-10`} aria-invalid={!!error || undefined} data-testid="auth-password-input" />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={showPw ? 'Hide password' : 'Show password'} data-testid="auth-password-toggle">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              </div>
            </div>
            {error && <p role="alert" className="text-xs text-destructive" data-testid="auth-error">{error}</p>}
            <Button type="submit" className="w-full h-11 text-sm font-semibold" disabled={busy || !email || password.length < (mode === 'signup' ? 8 : 1)} data-testid="auth-submit-button">
              {busy ? (mode === 'signin' ? 'Signing in…' : 'Creating your account…') : mode === 'signin' ? 'Sign in' : 'Create account'}
            </Button>
            {mode === 'signin' && (
              <div className="text-right">
                <button type="button" onClick={() => setMode('forgot')} className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="auth-forgot-link">Forgot password?</button>
              </div>
            )}
            {mode === 'signup' && (
              <p className="text-[11px] leading-snug text-muted-foreground text-center" data-testid="auth-consent">
                By creating an account you agree to the <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">Terms of Service</Link> and <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">Privacy Policy</Link>.
              </p>
            )}
          </form>
        </Tabs>
      )}
      {footer}
    </div>
  );
};
